import './style.css';
import { cloneBoard, Game, type ClearResult } from './engine/game';
import { FinesseTracker, isFault } from './engine/finesse';
import { DEFAULT_GEN_OPTIONS, splitHoldAndQueue, type GenOptions } from './generator/core';
import {
  TSPIN_MODES,
  createTSpinProgress,
  generateTSpinPuzzle,
  isTSpinWinningState,
  recordTSpinClear,
  type GeneratedTSpinPuzzle,
  type TSpinMode,
  type TSpinProgress,
} from './generator/tspin';
import { showToast, updateHud } from './ui/hud';
import { InputController, loadControlSettings, type Action, type ControlSettings } from './ui/input';
import { buildLayout } from './ui/layout';
import { renderBoard, renderHold, renderNext } from './ui/renderer';
import { initSettingsPanel } from './ui/settings';

const TSPIN_LABELS: Record<TSpinMode, string> = {
  tsd: 'T-Spin Double',
  stsd: 'Sequential T-Spin Double',
  tst: 'T-Spin Triple',
};

interface AppState {
  mode: TSpinMode;
  options: GenOptions;
  puzzle: GeneratedTSpinPuzzle;
  game: Game;
  controls: ControlSettings;
  pressCount: number;
  puzzleStartTime: number;
  elapsedMs: number;
  solved: boolean;
  transitioning: boolean;
  sessionFinesse: FinesseTracker;
  puzzleFinesse: FinesseTracker;
  sessionStartTime: number;
  puzzlesSolved: number;
  level: number;
  score: number;
  tspinProgress: TSpinProgress;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

function pickTSpinVariant(): TSpinMode {
  return TSPIN_MODES[Math.floor(Math.random() * TSPIN_MODES.length)];
}

// stsd (DT-Cannon, two T-spins) and tst (hand-designed shape) both have a
// much lower per-attempt success rate than tsd, so a generous retry budget
// is what actually makes every variant reliable in practice.
const GENERATION_RETRIES = 25;

function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, 0));
}

// Each individual attempt is synchronous (and, for stsd/tst, can itself take
// up to ~2s), so the retry loop yields to the event loop between attempts —
// otherwise a run of unlucky attempts blocks the main thread solid long
// enough that the tab looks hung and the "Generating…" toast never even gets
// a chance to paint.
async function generatePuzzleWithRetries(mode: TSpinMode, options: GenOptions): Promise<GeneratedTSpinPuzzle> {
  let lastError: unknown;
  for (let attempt = 0; attempt < GENERATION_RETRIES; attempt++) {
    try {
      return generateTSpinPuzzle(mode, options);
    } catch (err) {
      lastError = err;
    }
    await yieldToBrowser();
  }
  // Total failure is rare but possible — fall back to the most reliable
  // variant, with its own retry budget, rather than leaving the player on a
  // crashed page after a single unlucky fallback attempt.
  if (mode !== 'tsd') {
    for (let attempt = 0; attempt < GENERATION_RETRIES; attempt++) {
      try {
        return generateTSpinPuzzle('tsd', options);
      } catch (err) {
        lastError = err;
      }
      await yieldToBrowser();
    }
  }
  throw lastError;
}

function buildGameFromPuzzle(puzzle: GeneratedTSpinPuzzle): Game {
  const { hold, rest } = splitHoldAndQueue(puzzle.queue);
  const game = new Game(cloneBoard(puzzle.board), rest);
  game.holdPiece = hold;
  game.holdUsed = false;
  return game;
}

function scoreForClear(result: ClearResult, level: number): number {
  if (result.linesCleared === 0) return 0;
  const base = result.tspin
    ? ({ 1: result.tspin === 'mini' ? 200 : 800, 2: 1200, 3: 1600 } as Record<number, number>)
    : ({ 1: 100, 2: 300, 3: 500, 4: 800 } as Record<number, number>);
  let points = (base[result.linesCleared] ?? 0) * level;
  if (result.b2b > 0) points = Math.floor(points * 1.5);
  if (result.combo > 0) points += 50 * result.combo * level;
  if (result.perfectClear) points += 1000 * level;
  return points;
}

async function boot(): Promise<void> {
  const root = document.getElementById('app');
  if (!root) throw new Error('missing #app root element');
  const layout = buildLayout(root);

  function setControlsBusy(busy: boolean): void {
    layout.pieceCountInput.disabled = busy;
    layout.newPuzzleBtn.disabled = busy;
  }

  setControlsBusy(true);
  showToast(layout, 'Generating puzzle…', 20000);

  const initialMode = pickTSpinVariant();
  const initialPuzzle = await generatePuzzleWithRetries(initialMode, DEFAULT_GEN_OPTIONS);
  showToast(layout, 'Puzzle ready', 1000);
  setControlsBusy(false);
  const state: AppState = {
    mode: initialMode,
    options: { ...DEFAULT_GEN_OPTIONS },
    puzzle: initialPuzzle,
    game: buildGameFromPuzzle(initialPuzzle),
    controls: loadControlSettings(),
    pressCount: 0,
    puzzleStartTime: performance.now(),
    elapsedMs: 0,
    solved: false,
    transitioning: false,
    sessionFinesse: new FinesseTracker(),
    puzzleFinesse: new FinesseTracker(),
    sessionStartTime: performance.now(),
    puzzlesSolved: 0,
    level: 1,
    score: 0,
    tspinProgress: createTSpinProgress(),
  };

  layout.pieceCountInput.value = String(state.options.pieceCount);

  function render(): void {
    renderBoard(layout, state.game);
    renderHold(layout, state.game);
    renderNext(layout, state.game);
    layout.puzzleLabel.textContent = TSPIN_LABELS[state.mode].toUpperCase();
  }

  async function newPuzzle(): Promise<void> {
    state.transitioning = true; // block input while a (possibly slow) generation runs
    setControlsBusy(true);
    showToast(layout, 'Generating puzzle…', 20000);
    const nextMode = pickTSpinVariant();
    try {
      const nextPuzzle = await generatePuzzleWithRetries(nextMode, state.options);
      state.mode = nextMode;
      state.puzzle = nextPuzzle;
      state.game = buildGameFromPuzzle(state.puzzle);
      state.pressCount = 0;
      state.puzzleStartTime = performance.now();
      state.elapsedMs = 0;
      state.solved = false;
      state.puzzleFinesse.reset();
      state.tspinProgress = createTSpinProgress();
      render();
      showToast(layout, 'Puzzle ready', 1000);
    } catch {
      showToast(layout, 'Generation failed — try New Puzzle again', 4000);
    } finally {
      state.transitioning = false;
      setControlsBusy(false);
    }
  }

  function retryPuzzle(): void {
    state.transitioning = false;
    state.game = buildGameFromPuzzle(state.puzzle);
    state.pressCount = 0;
    state.puzzleStartTime = performance.now();
    state.elapsedMs = 0;
    state.solved = false;
    state.puzzleFinesse.reset();
    state.tspinProgress = createTSpinProgress();
    render();
  }

  function finishPuzzleAttempt(): void {
    const game = state.game;
    const win = !game.topOut && isTSpinWinningState(state.mode, game.board, state.tspinProgress);
    state.elapsedMs = performance.now() - state.puzzleStartTime;
    state.solved = win;
    state.transitioning = true;
    if (win) {
      state.puzzlesSolved += 1;
      state.level = 1 + Math.floor(state.puzzlesSolved / 5);
      showToast(layout, 'Solved!');
      window.setTimeout(newPuzzle, 900);
    } else {
      showToast(layout, game.topOut ? 'Topped out — retrying' : 'Not solved — retrying');
      window.setTimeout(retryPuzzle, 900);
    }
  }

  function handleHold(): void {
    if (state.game.hold()) render();
  }

  function handleHardDrop(): void {
    const game = state.game;
    const piece = game.current;
    if (!piece) return;
    const actualInputs = state.pressCount + 1;
    const fault = isFault(actualInputs, piece.piece, piece.orientation, piece.x);
    state.sessionFinesse.record(fault);
    state.puzzleFinesse.record(fault);

    const result = game.hardDrop();
    state.pressCount = 0;
    state.score += scoreForClear(result, state.level);
    recordTSpinClear(state.tspinProgress, result.linesCleared, result.tspin);

    if (game.topOut || game.isExhausted) finishPuzzleAttempt();
    render();
  }

  function handleAction(action: Action, isRepeat: boolean): void {
    if (state.transitioning) return;
    if (action === 'restart') {
      retryPuzzle();
      return;
    }
    const game = state.game;
    if (action === 'hold') {
      handleHold();
      return;
    }
    if (!game.current) return;

    if (action === 'hardDrop') {
      handleHardDrop();
      return;
    }

    let countsAsInput = false;
    switch (action) {
      case 'moveLeft':
        if (isRepeat && state.controls.arr === 0) game.moveToWall(-1);
        else game.moveLeft();
        countsAsInput = !isRepeat;
        break;
      case 'moveRight':
        if (isRepeat && state.controls.arr === 0) game.moveToWall(1);
        else game.moveRight();
        countsAsInput = !isRepeat;
        break;
      case 'softDrop':
        game.softDropStep();
        break;
      case 'rotateCW':
        game.rotateCW();
        countsAsInput = true;
        break;
      case 'rotateCCW':
        game.rotateCCW();
        countsAsInput = true;
        break;
      case 'rotate180':
        game.rotate180();
        countsAsInput = true;
        break;
    }
    if (countsAsInput) state.pressCount += 1;
    render();
  }

  const inputController = new InputController(handleAction, state.controls);

  initSettingsPanel(
    layout,
    () => state.controls,
    (next) => {
      state.controls = next;
      inputController.updateSettings(next);
    },
    (open) => inputController.setEnabled(!open),
  );

  layout.pieceCountInput.addEventListener('change', () => {
    const n = clamp(parseInt(layout.pieceCountInput.value, 10) || 7, 2, 7);
    layout.pieceCountInput.value = String(n);
    state.options = { ...state.options, pieceCount: n };
    newPuzzle();
  });
  layout.newPuzzleBtn.addEventListener('click', () => newPuzzle());

  function tick(): void {
    if (!state.solved && !state.transitioning) state.elapsedMs = performance.now() - state.puzzleStartTime;
    const sessionSeconds = (performance.now() - state.sessionStartTime) / 1000;
    updateHud(layout, {
      sessionPieces: state.sessionFinesse.pieces,
      sessionRate: sessionSeconds > 0 ? state.sessionFinesse.pieces / sessionSeconds : 0,
      sessionFinessePercent: state.sessionFinesse.percent,
      sessionFaults: state.sessionFinesse.faults,
      level: state.level,
      score: state.score,
      puzzlePieces: state.puzzleFinesse.pieces,
      puzzleFinessePercent: state.puzzleFinesse.percent,
      puzzleFaults: state.puzzleFinesse.faults,
      elapsedMs: state.elapsedMs,
      solved: state.solved,
    });
    requestAnimationFrame(tick);
  }

  render();
  requestAnimationFrame(tick);
}

boot().catch((err) => {
  console.error(err);
  document.getElementById('app')?.replaceChildren(document.createTextNode('Failed to start — reload the page.'));
});
